<script setup lang="ts">
import { computed } from "vue";
import type { ColorScheme } from "../constants";

const props = defineProps<{
  subHeading?: string;
  headingElement?: string;
  small?: boolean;
  colorScheme?: ColorScheme;
}>();

// Validate and get the proper heading element
const validHeadingElement = computed(() => {
  const validHeadings = ["h1", "h2", "h3", "h4", "h5", "h6"];

  if (
    props.headingElement &&
    typeof props.headingElement === "string" &&
    validHeadings.includes(props.headingElement.toLowerCase())
  ) {
    return props.headingElement.toLowerCase();
  }

  if (
    props.headingElement &&
    typeof props.headingElement === "string" &&
    !validHeadings.includes(props.headingElement.toLowerCase())
  ) {
    console.warn(
      `Invalid headingElement '${props.headingElement}' provided. Using default 'h2'. Valid options are: h1, h2, h3, h4, h5, h6`,
    );
  }

  return "h2"; // default
});
</script>

<template>
  <component
    :is="validHeadingElement"
    :class="[
      'heading-wrapper',
      props.small && 'heading-wrapper--small',
      props.colorScheme === 'LIGHT' && 'heading-wrapper--light',
    ]"
    data-testid="reco-heading"
  >
    <span v-if="props.subHeading" :class="['subheading', props.small && 'subheading--small']">
      {{ props.subHeading }}
    </span>
    <span :class="['heading', props.small && 'heading--small']">
      <slot />
    </span>
  </component>
</template>

<style lang="scss" scoped>
.heading-wrapper {
  font-family: "LidlFontPro", sans-serif;
  margin-top: 0;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100vw;

  &--small {
    align-items: flex-start;
    margin-bottom: 16px;
  }

  &--light {
    .heading,
    .subheading {
      color: #e3e4e5;
    }
  }
}

.heading {
  font-weight: 700;
  font-size: 1.8rem;
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;

  &--small {
    font-size: 1rem;
    text-align: left;
    font-weight: 600;
    margin: 0 0.5rem;
  }
}

.subheading {
  font-weight: 600;
  font-size: 1.125rem;
  margin: 0;
  text-align: center;
  width: 100%;

  &--small {
    font-size: 0.875rem;
    text-align: left;
    font-weight: 500;
  }
}

@media screen and (min-width: 1440px) {
  .heading {
    font-size: 2.875rem;

    &--small {
      font-size: 1.125rem;
    }
  }

  .subheading {
    font-size: 1.25rem;

    &--small {
      font-size: 1rem;
    }
  }
}
</style>
